import { initializeApp, type FirebaseApp } from "firebase/app";
import {
  getAuth,
  GoogleAuthProvider,
  signInWithPopup,
  type Auth,
  type UserCredential,
} from "firebase/auth";

/**
 * Firebase Auth para Nexus.
 *
 * Misma config que MNW-User (project mixcore-prod, mismo authDomain) —
 * el JWT firmado por Firebase se intercambia en `/users/google-authentication`
 * y allá el backend valida la allowlist Nexus (NEXUS_ALLOWED_EMAILS).
 *
 * Variables de entorno requeridas (VITE_FIREBASE_*):
 *   - VITE_FIREBASE_API_KEY              — público, identifica el proyecto
 *   - VITE_FIREBASE_AUTH_DOMAIN          — default mixcore-prod.firebaseapp.com
 *   - VITE_FIREBASE_PROJECT_ID           — default mixcore-prod
 *   - VITE_FIREBASE_MESSAGING_SENDER_ID
 *   - VITE_FIREBASE_APP_ID
 *
 * Importante: estas envs son "públicas" en el sentido OAuth — vienen en el
 * bundle JS final. La seguridad la pone el backend: aunque cualquiera
 * pueda iniciar Firebase Sign-In, sin estar en NEXUS_ALLOWED_EMAILS no
 * recibe JWT del backend.
 */

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || "mixcore-prod.firebaseapp.com",
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || "mixcore-prod",
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || "mixcore-prod.appspot.com",
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

let _app: FirebaseApp | null = null;
let _auth: Auth | null = null;
let _provider: GoogleAuthProvider | null = null;

function ensureInitialized(): { auth: Auth; provider: GoogleAuthProvider } {
  if (!_app) {
    if (!firebaseConfig.apiKey) {
      throw new Error(
        "Firebase no configurado: falta VITE_FIREBASE_API_KEY. Ver backoffice/.env.example.",
      );
    }
    _app = initializeApp(firebaseConfig);
    _auth = getAuth(_app);
    _provider = new GoogleAuthProvider();
    // Forzá la selección de cuenta cada vez — evita que el browser
    // auto-loguee con una cuenta sin permiso y muestre 403 confuso.
    _provider.setCustomParameters({ prompt: "select_account" });
  }
  return { auth: _auth!, provider: _provider! };
}

/**
 * Abre el popup de Google Sign-In y devuelve el ID token de Firebase.
 *
 * El caller debe enviar ese token al backend (POST /users/google-authentication
 * con body `{ token, portal: "nexus" }`) para que valide la allowlist y
 * devuelva el JWT propio de Mixcore.
 */
export async function signInWithGoogle(): Promise<{
  idToken: string;
  email: string;
  displayName: string;
  photoURL: string | null;
}> {
  const { auth, provider } = ensureInitialized();
  const result: UserCredential = await signInWithPopup(auth, provider);
  const idToken = await result.user.getIdToken();
  return {
    idToken,
    email: result.user.email ?? "",
    displayName: result.user.displayName ?? "",
    photoURL: result.user.photoURL,
  };
}
